import { FormikContextType } from "formik";
import {
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    WorkStartDateMode,
    WorkEndDateMode,
} from "@common/types/serviceTemplateRecurrence";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { VStack } from "@design-system/VStack/VStack";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import {
    SUPPORTED_WORK_END_MODES,
    SUPPORTED_WORK_START_MODES,
    WEEKDAY_LABELS,
    WEEKDAY_OPTIONS,
} from "./serviceTemplateSchedule.constants";
import styles from "./ScheduleTemplateDrawer.module.scss";

interface Props {
    formik: FormikContextType<ApplyCloseTemplateWizardValues>;
}

const WORK_START_LABELS: Record<WorkStartDateMode, string> = {
    [WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END]: "Days after service end",
    [WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END]:
        "Next weekday after service end",
    [WorkStartDateMode.RELATIVE_CALENDAR_DATE_AFTER_SERVICE_END]:
        "Relative calendar date after service end",
};

const WORK_END_LABELS: Record<WorkEndDateMode, string> = {
    [WorkEndDateMode.OFFSET_DAYS_AFTER_WORK_START]: "Days after work start",
    [WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START]:
        "Last weekday of the work month",
};

const WORK_START_ITEMS: Array<PickerItemData<WorkStartDateMode>> =
    SUPPORTED_WORK_START_MODES.map((mode) => ({
        value: mode,
        label: WORK_START_LABELS[mode],
    }));

const WORK_END_ITEMS: Array<PickerItemData<WorkEndDateMode>> =
    SUPPORTED_WORK_END_MODES.map((mode) => ({
        value: mode,
        label: WORK_END_LABELS[mode],
    }));

export const ScheduleTemplateStepWork = ({ formik }: Props) => {
    const schedule = formik.values.defaults.schedule;
    if (!schedule) {
        return null;
    }

    const showStartOffset =
        schedule.workStartDateMode ===
        WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END;
    const showNextWeekday =
        schedule.workStartDateMode ===
        WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END;
    const showEndOffset =
        schedule.workEndDateMode ===
        WorkEndDateMode.OFFSET_DAYS_AFTER_WORK_START;
    const showEndRelative =
        schedule.workEndDateMode ===
        WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START;

    return (
        <VStack gap={16}>
            <div>
                <label
                    className={styles.fieldLabel}
                    htmlFor="st-workStartDateMode"
                >
                    Work start date mode
                </label>
                <Picker.Select
                    id="st-workStartDateMode"
                    items={WORK_START_ITEMS}
                    value={schedule.workStartDateMode}
                    renderValue={([mode]) => WORK_START_LABELS[mode]}
                    trigger={
                        <Picker.Button variant="primary" size="xl" block />
                    }
                    onValueChange={(v) => {
                        if (v) {
                            void formik.setFieldValue(
                                "defaults.schedule.workStartDateMode",
                                v,
                            );
                            // Reset every mode-specific field on switch so a
                            // stale value from the previous mode can't be
                            // submitted alongside the new mode (mirrors the
                            // backend's nullify-inapplicable-siblings rule).
                            void formik.setFieldValue(
                                "defaults.schedule.workStartOffsetDays",
                                0,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workStartSkipWeekend",
                                false,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workStartWeekday",
                                null,
                            );
                        }
                    }}
                />
            </div>

            {showStartOffset ? (
                <VStack gap={16}>
                    <Field
                        className={styles.offsetField}
                        label="Work start offset (days)"
                    >
                        <Input
                            id="st-workStartOffsetDays"
                            type="number"
                            size="xl"
                            value={schedule.workStartOffsetDays ?? 0}
                            onChange={(event) => {
                                void formik.setFieldValue(
                                    "defaults.schedule.workStartOffsetDays",
                                    parseInt(event.target.value, 10) || 0,
                                );
                            }}
                        />
                    </Field>
                    <Checkbox
                        checked={schedule.workStartSkipWeekend}
                        label="In case of weekend next week day"
                        onCheckedChange={(checked) =>
                            void formik.setFieldValue(
                                "defaults.schedule.workStartSkipWeekend",
                                checked,
                            )
                        }
                    />
                </VStack>
            ) : null}

            {showNextWeekday ? (
                <div>
                    <label
                        className={styles.fieldLabel}
                        htmlFor="st-workStartWeekday"
                    >
                        Work start weekday
                    </label>
                    <Picker.Select
                        id="st-workStartWeekday"
                        items={WEEKDAY_OPTIONS}
                        value={schedule.workStartWeekday}
                        onValueChange={(v) =>
                            void formik.setFieldValue(
                                "defaults.schedule.workStartWeekday",
                                v ?? null,
                            )
                        }
                        placeholder="Select day..."
                        renderValue={([weekday]) => WEEKDAY_LABELS[weekday]}
                        trigger={
                            <Picker.Button variant="primary" size="xl" block />
                        }
                    />
                </div>
            ) : null}

            <div>
                <label
                    className={styles.fieldLabel}
                    htmlFor="st-workEndDateMode"
                >
                    Work end date mode
                </label>
                <Picker.Select
                    id="st-workEndDateMode"
                    items={WORK_END_ITEMS}
                    value={schedule.workEndDateMode}
                    renderValue={([mode]) => WORK_END_LABELS[mode]}
                    trigger={
                        <Picker.Button variant="primary" size="xl" block />
                    }
                    onValueChange={(v) => {
                        if (!v) {
                            return;
                        }
                        void formik.setFieldValue(
                            "defaults.schedule.workEndDateMode",
                            v,
                        );
                        if (
                            v ===
                            WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START
                        ) {
                            void formik.setFieldValue(
                                "defaults.schedule.workEndOffsetDays",
                                null,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workEndRelativePeriod",
                                RelativeCalendarPeriod.MONTH,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workEndRelativePosition",
                                RelativeCalendarPosition.SAME,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workEndRelativeDayType",
                                RelativeDayType.LAST,
                            );
                            void formik.setFieldValue(
                                "defaults.schedule.workEndRelativeDayOfPeriod",
                                null,
                            );
                            return;
                        }
                        void formik.setFieldValue(
                            "defaults.schedule.workEndOffsetDays",
                            0,
                        );
                        void formik.setFieldValue(
                            "defaults.schedule.workEndRelativePeriod",
                            null,
                        );
                        void formik.setFieldValue(
                            "defaults.schedule.workEndRelativePosition",
                            null,
                        );
                        void formik.setFieldValue(
                            "defaults.schedule.workEndRelativeDayType",
                            null,
                        );
                        void formik.setFieldValue(
                            "defaults.schedule.workEndRelativeDayOfPeriod",
                            null,
                        );
                    }}
                />
            </div>

            {showEndOffset ? (
                <Field
                    className={styles.offsetField}
                    label="Work end offset (days)"
                >
                    <Input
                        id="st-workEndOffsetDays"
                        type="number"
                        size="xl"
                        value={schedule.workEndOffsetDays ?? 0}
                        onChange={(event) => {
                            void formik.setFieldValue(
                                "defaults.schedule.workEndOffsetDays",
                                parseInt(event.target.value, 10) || 0,
                            );
                        }}
                    />
                </Field>
            ) : null}

            {showEndRelative ? (
                <p className={styles.helperText}>
                    Work ends on the last weekday of the month that contains
                    work start.
                </p>
            ) : null}
        </VStack>
    );
};
